import { useQueryClient } from '@tanstack/react-query';
import { Alert, DatePicker, Form, Input, Modal, Table, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useShipmentsTransition } from '@/api/generated/endpoints/shipments/shipments';
import type { ProductRef, ShipmentDetail, ShipmentsTransitionBody } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { QtyCell } from '@/components/qty';
import { problemMessage } from '@/i18n/problem';
import { invalidatePath } from '@/lib/queryCache';
import type { SimpleTarget } from '@/pages/shipments/transitions';

interface Shortage {
  product: ProductRef;
  requested: string;
  available: string;
  missing: string;
  in_expired: string;
  line_nos: number[];
}

interface Values {
  comment?: string;
  shipped_at?: Dayjs | null;
}

export function TransitionModal({
  shipment,
  target,
  onClose,
  onDone,
}: {
  shipment: ShipmentDetail;
  target: SimpleTarget | null;
  onClose: () => void;
  onDone: (s: ShipmentDetail) => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<Values>();
  const [error, setError] = useState<string | null>(null);
  const [shortages, setShortages] = useState<Shortage[] | null>(null);
  const queryClient = useQueryClient();
  const refresh = () => invalidatePath(queryClient, '/api/v1/shipments');

  const transition = useShipmentsTransition({
    mutation: {
      onSuccess: async (s) => {
        // Резерв и отгрузка меняют остатки партий.
        await Promise.all([
          refresh(),
          invalidatePath(queryClient, '/api/v1/stock'),
          invalidatePath(queryClient, '/api/v1/batches'),
        ]);
        close();
        onDone(s);
      },
      onError: async (e) => {
        if (e instanceof ApiProblem && e.code === 'stock_shortage') {
          setShortages((e.extra.shortages as Shortage[] | undefined) ?? []);
          return;
        }
        if (e instanceof ApiProblem && e.code === 'version_conflict') {
          await refresh(); // свежие данные под модалкой — человек видит, что изменилось
          setError(t('shipment.versionConflict'));
          return;
        }
        setError(problemMessage(e));
      },
    },
  });

  function close() {
    setError(null);
    setShortages(null);
    form.resetFields();
    onClose();
  }

  function submit(v: Values) {
    if (!target) return;
    setError(null);
    setShortages(null);
    const comment = v.comment?.trim() ? v.comment.trim() : null;
    const base = { version: shipment.version };
    let data: ShipmentsTransitionBody;
    if (target === 'cancelled') data = { ...base, to: 'cancelled', comment: comment ?? '' };
    else if (target === 'shipped') {
      data = { ...base, to: 'shipped', comment, shipped_at: v.shipped_at?.toISOString() ?? null };
    } else data = { ...base, to: target, comment };
    transition.mutate({ shipmentId: shipment.id, data });
  }

  return (
    <Modal
      title={target ? t(`transition.${target}`) : ''}
      open={target !== null}
      onCancel={close}
      // Отгрузка и отмена — кнопка называет действие и номер (ADR-0011 п. 6).
      okText={
        target === 'shipped' || target === 'cancelled'
          ? t(`transition.confirm_${target}`, { number: shipment.number })
          : t('transition.confirm')
      }
      okButtonProps={{ loading: transition.isPending, danger: target === 'cancelled' }}
      onOk={() => {
        form.submit();
      }}
      width={shortages ? 760 : 520}
      destroyOnHidden
    >
      {target && (
        <Typography.Paragraph type="secondary">
          {t(`transition.${target}Hint`)}
        </Typography.Paragraph>
      )}
      {(target === 'shipped' || target === 'cancelled') && (
        <Alert
          type={target === 'cancelled' ? 'warning' : 'info'}
          style={{ marginBottom: 16 }}
          message={t('transition.summary', {
            number: shipment.number,
            client: shipment.client.brand ?? shipment.client.name,
            lines: shipment.items.length,
          })}
        />
      )}
      <Form<Values>
        form={form}
        layout="vertical"
        initialValues={{ shipped_at: dayjs() }}
        onFinish={submit}
      >
        {target === 'shipped' && (
          <Form.Item name="shipped_at" label={t('transition.shippedAt')}>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD.MM.YYYY HH:mm"
              disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            />
          </Form.Item>
        )}
        <Form.Item
          name="comment"
          label={t('transition.comment')}
          rules={
            target === 'cancelled'
              ? [{ required: true, whitespace: true, message: t('transition.commentRequired') }]
              : []
          }
        >
          <Input.TextArea maxLength={2000} autoSize={{ minRows: 1, maxRows: 4 }} />
        </Form.Item>
      </Form>
      {shortages && <ShortageTable shortages={shortages} />}
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}

/** Отчёт о дефиците (409 stock_shortage): по товару целиком, ничего не зарезервировано. */
function ShortageTable({ shortages }: { shortages: Shortage[] }) {
  const { t } = useTranslation();
  return (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 12 }}
      message={t('transition.shortageTitle')}
      description={
        <Table<Shortage>
          rowKey={(s) => s.product.id}
          size="small"
          pagination={false}
          dataSource={shortages}
          columns={[
            { key: 'product', title: t('transition.colProduct'), render: (_, s) => s.product.name },
            {
              key: 'requested',
              title: t('transition.colRequested'),
              render: (_, s) => <QtyCell value={s.requested} uom={s.product.base_uom} />,
            },
            {
              key: 'available',
              title: t('transition.colAvailable'),
              render: (_, s) => <QtyCell value={s.available} uom={s.product.base_uom} />,
            },
            {
              key: 'missing',
              title: t('transition.colMissing'),
              render: (_, s) => (
                <Typography.Text type="danger" strong>
                  <QtyCell value={s.missing} uom={s.product.base_uom} />
                </Typography.Text>
              ),
            },
            {
              key: 'expired',
              title: t('transition.colExpired'),
              render: (_, s) => <QtyCell value={s.in_expired} uom={s.product.base_uom} />,
            },
            {
              key: 'lines',
              title: t('transition.colLines'),
              render: (_, s) => s.line_nos.join(', '),
            },
          ]}
        />
      }
    />
  );
}
